/* Homepage hero wordmark: shrink + fly-up once the page leaves the top.
 *
 * The tween itself is a CSS transition (assets/hero-logo.css). This script only
 *   1. measures, once per viewport width, the scale that lands the wordmark at
 *      TARGET_HEIGHT and the fly-up distance in px,
 *   2. toggles .is-compact when the page scrolls past COMPACT_AT, and back only
 *      when it returns to EXPAND_AT, and
 *   3. holds the entrance back (.is-waiting) while the hero photo is still
 *      loading, so on a cold visit the wordmark doesn't jump in over the black
 *      placeholder. The entrance itself is CSS and plays from first paint; a
 *      photo already in memory (any return to the home page) needs nothing
 *      from here. The slideshow sections inline this script AFTER the slides
 *      so the photo element exists here. A photo that takes longer than
 *      REVEAL_TIMEOUT (or fails) no longer holds the wordmark back.
 * Nothing runs per frame. A busy main thread (in-app browsers, image decodes,
 * third-party scripts) can delay the trigger, but never the tween: the
 * compositor runs it at full frame rate on its own.
 *
 * Two things here exist for in-app browsers (Instagram) on a SLOW scroll:
 * - The gap between COMPACT_AT and EXPAND_AT. Their bars collapse while you
 *   scroll near the top and nudge scrollY by a few px either way. A single
 *   threshold restarts the 1.5s tween on every nudge, which reads as a
 *   stutter. With the band, the wordmark only grows back at the very top.
 * - The fly-up distance is a px value measured once, not a vh unit, and
 *   height-only resizes are ignored. A viewport-height change mid-tween would
 *   otherwise retarget the running transition: same stutter.
 */
(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const overlay = document.querySelector('.hero-logo-overlay');
  const art = overlay && overlay.querySelector('.hero-logo-overlay__img');
  if (!overlay || !art) return;

  const TARGET_HEIGHT = 24; // px tall when compact
  const COMPACT_AT = 12; // px scrolled down: start the shrink
  const EXPAND_AT = 2; // px from the top: grow back
  const EXIT_VH = 10; // fly-up distance as a share of the viewport height
  const ENTRANCE_HOLD = 150; // ms the entrance keyframes stay at opacity 0
  const REVEAL_TIMEOUT = 3000; // ms: show the wordmark even without the photo

  let compact = null; // unknown until the first sync()
  let measuredWidth = 0;
  let interacted = false; // any touch / wheel / key / pointer so far

  const scrollTop = () =>
    window.scrollY || document.documentElement.scrollTop || 0;

  // Only touch the inline style when the value really changes: a same-value
  // write is still a style invalidation in some engines.
  const setVar = (name, value) => {
    if (overlay.style.getPropertyValue(name) === value) return;
    overlay.style.setProperty(name, value);
  };

  // Runs once per viewport WIDTH (first paint, orientation change, desktop
  // window resize). offsetHeight is the layout size: transforms don't affect
  // it, so this is stable even mid-tween.
  function measure() {
    if (window.innerWidth === measuredWidth) return;
    measuredWidth = window.innerWidth;

    const h = art.offsetHeight;
    if (h) {
      setVar(
        '--hero-logo-small-scale',
        String(Math.min(1, TARGET_HEIGHT / h)),
      );
    }
    const exit = Math.round((window.innerHeight * EXIT_VH) / 100);
    if (exit > 0) setVar('--hero-logo-exit-y', `-${exit}px`);
  }

  function sync(animate) {
    const y = scrollTop();
    const next = compact ? y > EXPAND_AT : y > COMPACT_AT;
    if (next === compact) return;
    compact = next;

    if (animate) {
      overlay.classList.toggle('is-compact', next);
      return;
    }

    // Snap: landing on an already-scrolled page (reload, anchor, bfcache)
    // must not play the tween.
    overlay.classList.add('is-static');
    overlay.classList.toggle('is-compact', next);
    void overlay.offsetWidth; // flush the static state before re-enabling
    overlay.classList.remove('is-static');
  }

  measure();
  sync(false);

  // Entrance: the CSS has been playing it since first paint. A photo still
  // loading holds the wordmark until it is in; taking .is-waiting off replays
  // the entrance from the start. If the jump-in has already begun (this
  // script ran late), it stays: hiding it again would read as a flash.
  const root = overlay.closest('slideshow-component') || overlay.parentElement;
  const hero = root && root.querySelector('.slideshow__media img');
  const entrance = art.getAnimations ? art.getAnimations()[0] : null;
  const begun = entrance && entrance.currentTime > ENTRANCE_HOLD;
  if (hero && !hero.complete && !begun) {
    const release = () => overlay.classList.remove('is-waiting');
    overlay.classList.add('is-waiting');
    hero.addEventListener('load', release, { once: true });
    hero.addEventListener('error', release, { once: true });
    setTimeout(release, REVEAL_TIMEOUT);
  }

  // A scroll the visitor did not make (restored position on back/forward or
  // reload, an anchor) snaps; the tween is for scrolling they do themselves.
  const noteInput = () => {
    interacted = true;
  };
  ['touchstart', 'wheel', 'keydown', 'pointerdown'].forEach((type) =>
    window.addEventListener(type, noteInput, { passive: true, capture: true }),
  );

  window.addEventListener('scroll', () => sync(interacted), { passive: true });
  window.addEventListener('resize', measure, { passive: true });
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    measure();
    sync(false);
  });
})();
